import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface EnhancedStatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<any>;
  gradient: string;
  trend?: number;
  description?: string;
  percentage?: number;
}

const EnhancedStatCard: React.FC<EnhancedStatCardProps> = ({ 
  title, 
  value, 
  icon: Icon, 
  gradient, 
  trend, 
  description, 
  percentage 
}) => (
  <div className="group relative overflow-hidden bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 sm:hover:-translate-y-2">
    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
    
    <div className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 w-16 sm:w-24 h-16 sm:h-24 bg-gradient-to-br from-white/10 to-white/5 rounded-full blur-xl group-hover:scale-150 transition-transform duration-700"></div>
    
    <div className="relative p-4 sm:p-6 lg:p-8">
      <div className="flex items-start justify-between mb-4 sm:mb-6">
        <div className="flex-1">
          <p className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider mb-1 sm:mb-2">{title}</p>
          <p className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 mb-1 group-hover:scale-110 transition-transform duration-300">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {trend !== undefined && (
            <div className="flex items-center gap-2">
              {trend > 0 ? (
                <TrendingUp className="w-4 h-4 text-green-500" />
              ) : trend < 0 ? (
                <TrendingDown className="w-4 h-4 text-red-500" />
              ) : (
                <div className="w-4 h-4 bg-gray-300 rounded-full" />
              )}
              <span className={`text-xs sm:text-sm font-medium ${
                trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-600'
              }`}>
                {Math.abs(trend)}% vs période précédente
              </span>
            </div>
          )}
          {description && (
            <p className="text-xs text-gray-400 mt-1 sm:mt-2 hidden sm:block">{description}</p>
          )}
        </div>
        
        <div className="relative">
          <div className={`absolute inset-0 bg-gradient-to-r ${gradient} rounded-xl sm:rounded-2xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-300`}></div>
          <div className={`relative p-2 sm:p-3 lg:p-4 bg-gradient-to-r ${gradient} rounded-xl sm:rounded-2xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-300`}>
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" />
          </div>
        </div>
      </div>
      
      {percentage !== undefined && (
        <div className="w-full bg-gray-100 rounded-full h-1.5 sm:h-2 mb-3 sm:mb-4">
          <div className={`bg-gradient-to-r ${gradient} h-1.5 sm:h-2 rounded-full transition-all duration-1000 group-hover:w-full`} 
               style={{width: `${Math.min(percentage, 100)}%`}}></div>
        </div>
      )}
      
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-400">Mis à jour maintenant</span>
        <div className="flex items-center space-x-1">
          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span className="text-green-600 font-medium hidden sm:inline">Temps réel</span>
        </div>
      </div>
    </div>
  </div>
);

export default EnhancedStatCard;